import axios from 'axios'
import { useCallback, useEffect, useState } from 'react'
import { useQuestionsInsert } from '../api/hooks/Questions/useQuestionsInsert'

export const useQuizCreateData = () => {
  const { insertQuestion, isPending, error, isSuccess } = useQuestionsInsert()
  const baseUrl = process.env.REACT_APP_API_URL

  const [levelOptions, setLevelOptions] = useState([])
  const [mainOptions, setMainOptions] = useState([])
  const [subOptions, setSubOptions] = useState([])
  const [unitOptions, setUnitOptions] = useState([])
  const [detailUnitOptions, setDetailUnitOptions] = useState([])

  const [chapterMeta, setChapterMeta] = useState({
    depth2: '',
    depth3: '',
    depth4: '',
    depth5: '',
    no: '',
  })

  const [curriculum, setCurriculum] = useState({
    level: '',
    mainChapter: '',
    subChapter: '',
    unit: '',
    detailUnit: '',
    count: '',
    questionLabel: '',
    selectedType: 'A',
  })

  const [quizData, setQuizData] = useState({
    question: '',
    type2: '',
    correctAnswer: '',
    correctAnswers: ['', '', '', '', ''],
    explanation: '',
    answer1: '',
    answer2: '',
    answer3: '',
    answer4: '',
    answer5: '',
    isCorrect1: false,
    isCorrect2: false,
    isCorrect3: false,
    isCorrect4: false,
    isCorrect5: false,
    checkedStates: [false, false, false, false, false],
    questionImageFile: null,
    subQuestionImageFile: null,
    questionImageFile1: null,
    questionImageFile2: null,
    answerImageFile: null,
    answerImageFiles: [null, null, null, null, null],
    explanationImageFile: null,
  })

  useEffect(() => {
    fetchLevelOptions()
  }, [])

  const generateQuestionLabel = (curr, meta) => {
    const numericLevel = curr.level.replace(/level\s*/i, '').trim()
    return `${numericLevel}-${meta.depth2}-${meta.depth3}-${meta.depth4}-${meta.depth5}-${meta.no}`
  }

  const generateSimpleQuestionLabel = (curr) => {
    if (
      !curr.level ||
      !curr.mainChapter ||
      !curr.subChapter ||
      !curr.unit ||
      !curr.detailUnit
    ) {
      return ''
    }

    const levelNum =
      curr.level
        .toString()
        .replace(/[^0-9]/g, '')
        .slice(-1) || '0'
    const mainNum =
      curr.mainChapter
        .toString()
        .replace(/[^0-9]/g, '')
        .slice(-1) || '0'
    const subNum =
      curr.subChapter
        .toString()
        .replace(/[^0-9]/g, '')
        .slice(-1) || '0'
    const unitNum =
      curr.unit
        .toString()
        .replace(/[^0-9]/g, '')
        .slice(-1) || '0'
    const detailNum =
      curr.detailUnit
        .toString()
        .replace(/[^0-9]/g, '')
        .slice(-1) || '0'

    const questionLabel = `${levelNum}${mainNum}${subNum}${unitNum}${detailNum}`

    return questionLabel
  }

  const handleQuestionLabelChange = useCallback((newLabel) => {
    setCurriculum((prev) => ({
      ...prev,
      questionLabel: newLabel,
    }))
  }, [])

  const updateQuestionLabel = useCallback((newLabel) => {
    setCurriculum((prev) => ({
      ...prev,
      questionLabel: newLabel,
    }))
  }, [])

  useEffect(() => {
    if (
      curriculum.level &&
      curriculum.mainChapter &&
      curriculum.subChapter &&
      curriculum.unit &&
      curriculum.detailUnit
    ) {
      const simpleLabel = generateSimpleQuestionLabel(curriculum)
      if (simpleLabel && simpleLabel !== curriculum.questionLabel) {
        setCurriculum((prev) => ({
          ...prev,
          questionLabel: simpleLabel,
        }))
      }
    }
  }, [
    curriculum.level,
    curriculum.mainChapter,
    curriculum.subChapter,
    curriculum.unit,
    curriculum.detailUnit,
  ])

  const updateChapterMetaFromCurriculum = useCallback(() => {
    if (
      curriculum.level &&
      curriculum.mainChapter &&
      curriculum.subChapter &&
      curriculum.unit &&
      curriculum.detailUnit
    ) {
      const levelNum =
        curriculum.level
          .toString()
          .replace(/[^0-9]/g, '')
          .slice(-1) || '0'
      const mainNum =
        curriculum.mainChapter
          .toString()
          .replace(/[^0-9]/g, '')
          .slice(-1) || '0'
      const subNum =
        curriculum.subChapter
          .toString()
          .replace(/[^0-9]/g, '')
          .slice(-1) || '0'
      const unitNum =
        curriculum.unit
          .toString()
          .replace(/[^0-9]/g, '')
          .slice(-1) || '0'
      const detailNum =
        curriculum.detailUnit
          .toString()
          .replace(/[^0-9]/g, '')
          .slice(-1) || '0'

      const newMeta = {
        depth2: mainNum,
        depth3: subNum,
        depth4: unitNum,
        depth5: detailNum,
        no: curriculum.count || '1',
      }
      setChapterMeta(newMeta)
    }
  }, [curriculum, chapterMeta])

  useEffect(() => {
    updateChapterMetaFromCurriculum()
  }, [
    curriculum.level,
    curriculum.mainChapter,
    curriculum.subChapter,
    curriculum.unit,
    curriculum.detailUnit,
    curriculum.count,
  ])

  const handleLevelChange = useCallback((value) => {
    setCurriculum((prev) => ({ ...prev, level: value || '' }))
    if (value) {
      fetchMainOptions(value)
    }
    setMainOptions([])
    setSubOptions([])
    setUnitOptions([])
    setDetailUnitOptions([])
  }, [])

  const handleMainChapterChange = useCallback(
    (value, option) => {
      const main = value || ''
      const depth = option?.depth || ''

      setCurriculum((prev) => {
        const updated = { ...prev, mainChapter: main }
        return {
          ...updated,
          questionLabel: generateQuestionLabel(updated, {
            ...chapterMeta,
            depth2: depth,
          }),
        }
      })

      setChapterMeta((prev) => ({ ...prev, depth2: depth }))

      if (main) {
        fetchSubOptions(curriculum.level, main)
      }

      setSubOptions([])
      setUnitOptions([])
      setDetailUnitOptions([])
    },
    [curriculum.level, chapterMeta]
  )

  const handleSubChapterChange = useCallback(
    (value, option) => {
      const sub = value || ''
      const depth = option?.depth || ''

      setCurriculum((prev) => {
        const updated = { ...prev, subChapter: sub }
        return {
          ...updated,
          questionLabel: generateQuestionLabel(updated, {
            ...chapterMeta,
            depth3: depth,
          }),
        }
      })

      setChapterMeta((prev) => ({ ...prev, depth3: depth }))

      if (sub) {
        fetchUnitOptions(curriculum.level, curriculum.mainChapter, sub)
      }

      setUnitOptions([])
      setDetailUnitOptions([])
    },
    [curriculum.level, curriculum.mainChapter, chapterMeta]
  )

  const handleUnitChange = useCallback(
    (value, option) => {
      const unit = value || ''
      const depth = option?.depth || ''

      setCurriculum((prev) => {
        const updated = { ...prev, unit }
        return {
          ...updated,
          detailUnit: '',
          questionLabel: generateQuestionLabel(updated, {
            ...chapterMeta,
            depth4: depth,
            depth5: '',
          }),
        }
      })

      setChapterMeta((prev) => ({ ...prev, depth4: depth, depth5: '' }))

      const { level, mainChapter, subChapter } = curriculum
      if (level && mainChapter && subChapter && unit) {
        fetchDetailUnitOptions(level, mainChapter, subChapter, unit)
      }

      setDetailUnitOptions([])
    },
    [curriculum, chapterMeta]
  )

  const handleDetailUnitChange = useCallback(
    (value, option) => {
      const detail = value || ''
      const depth = option?.depth || ''
      setCurriculum((prev) => {
        const updated = { ...prev, detailUnit: detail }
        return {
          ...updated,
          questionLabel: generateQuestionLabel(updated, {
            ...chapterMeta,
            depth5: depth,
          }),
        }
      })
      setChapterMeta((prev) => ({ ...prev, depth5: depth }))
    },
    [chapterMeta]
  )

  const handleCountChange = useCallback(
    (value) => {
      const numericValue = value.replace(/[^0-9]/g, '')
      const number = parseInt(numericValue) || 0

      if (numericValue === '' || (number >= 0 && number <= 100)) {
        const updatedMeta = { ...chapterMeta, no: numericValue }
        setChapterMeta(updatedMeta)

        setCurriculum((prev) => {
          const updated = { ...prev, count: numericValue }
          return {
            ...updated,
            questionLabel: generateQuestionLabel(updated, updatedMeta),
          }
        })
      }
    },
    [chapterMeta]
  )

  const handleTypeChange = useCallback((value) => {
    if (value) {
      setCurriculum((prev) => {
        const result = { ...prev, selectedType: value }
        return result
      })
    }
  }, [])

  const handleQuestionChange = useCallback((value) => {
    setQuizData((prev) => {
      const result = { ...prev, question: value }
      return result
    })
  }, [])

  // 중복 제거 헬퍼 함수
  const removeDuplicates = (data, keyField) => {
    if (!Array.isArray(data)) return []

    // null 값은 먼저 제거하고 중복 제거
    const validData = data.filter(
      (item) => item && item[keyField] !== null && item[keyField] !== undefined
    )

    return validData.filter(
      (item, index, self) =>
        index === self.findIndex((t) => t[keyField] === item[keyField])
    )
  }

  const fetchLevelOptions = async () => {
    try {
      const res = await axios.post(`${baseUrl}/chapters/list/levels`)

      const uniqueData = removeDuplicates(res.data.data, 'LEVEL')
      setLevelOptions(uniqueData.map((item) => `level ${item.LEVEL}`))
    } catch (error) {
      console.error('Level 옵션 fetch 에러:', error)
    }
  }

  const fetchMainOptions = async (level) => {
    try {
      const numericLevel = level.replace('level ', '').trim()
      const res = await axios.post(`${baseUrl}/chapters/list/mains`, {
        LEVEL: numericLevel,
      })

      const uniqueData = removeDuplicates(res.data.data, 'MAIN')

      const filteredData = uniqueData.filter(
        (item) => item && item.MAIN !== null && item.MAIN !== undefined
      )

      const options = filteredData.map((item) => ({
        value: String(item.MAIN),
        label: String(item.MAIN),
        depth: String(item.DEPTH2),
      }))

      setMainOptions(options)
    } catch (error) {
      console.error('Main 옵션 fetch 에러:', error)
    }
  }

  const fetchSubOptions = async (LEVEL, MAIN) => {
    try {
      const numericLevel = LEVEL.replace('level ', '').trim()
      const res = await axios.post(`${baseUrl}/chapters/list/subs`, {
        LEVEL: numericLevel,
        MAIN: MAIN,
      })

      const uniqueData = removeDuplicates(res.data.data, 'SUB')
      setSubOptions(
        uniqueData.map((item) => ({
          value: item.SUB,
          label: item.SUB,
          depth: item.DEPTH3.toString(),
        }))
      )
    } catch (error) {
      console.error('Sub 옵션 fetch 에러:', error)
    }
  }

  const fetchUnitOptions = async (LEVEL, MAIN, SUB) => {
    try {
      const numericLevel = LEVEL.replace('level ', '').trim()
      const res = await axios.post(`${baseUrl}/chapters/list/units`, {
        LEVEL: numericLevel,
        MAIN: MAIN,
        SUB: SUB,
      })

      const uniqueData = removeDuplicates(res.data.data, 'UNIT')
      setUnitOptions(
        uniqueData.map((item) => ({
          value: item.UNIT,
          label: item.UNIT,
          depth: item.DEPTH4.toString(),
        }))
      )
    } catch (error) {
      console.error('Unit 옵션 fetch 에러:', error)
    }
  }

  const fetchDetailUnitOptions = async (LEVEL, MAIN, SUB, UNIT) => {
    try {
      const numericLevel = LEVEL.replace('level ', '').trim()
      const res = await axios.post(`${baseUrl}/chapters/list/detail_units`, {
        LEVEL: numericLevel,
        MAIN: MAIN,
        SUB: SUB,
        UNIT: UNIT,
      })

      const uniqueData = removeDuplicates(res.data.data, 'DETAIL_UNIT')

      setDetailUnitOptions(
        uniqueData.map((item) => ({
          value: item.DETAIL_UNIT,
          label: item.DETAIL_UNIT,
          depth: item.DEPTH5.toString(),
        }))
      )
    } catch (error) {
      console.error('DetailUnit 옵션 fetch 에러:', error)
    }
  }

  const handleQuestionImageChange = useCallback((file) => {
    setQuizData((prev) => ({ ...prev, questionImageFile: file }))
  }, [])

  const handleSubQuestionImageChange = useCallback((file) => {
    setQuizData((prev) => ({ ...prev, subQuestionImageFile: file }))
  }, [])

  const handleQuestionImage1Change = useCallback((file) => {
    setQuizData((prev) => ({ ...prev, questionImageFile1: file }))
  }, [])

  const handleQuestionImage2Change = useCallback((file) => {
    setQuizData((prev) => ({ ...prev, questionImageFile2: file }))
  }, [])

  const handleCorrectAnswerChange = useCallback((value) => {
    setQuizData((prev) => {
      const result = { ...prev, correctAnswer: value }
      return result
    })
  }, [])

  const handleCorrectAnswersChange = useCallback((values) => {
    setQuizData((prev) => ({
      ...prev,
      correctAnswers: values,
      correctAnswer: values[0] || '',
      answer1: values[0] || '',
      answer2: values[1] || '',
      answer3: values[2] || '',
      answer4: values[3] || '',
      answer5: values[4] || '',
    }))
  }, [])

  const handleAnswer1Change = useCallback((value) => {
    setQuizData((prev) => ({ ...prev, answer1: value }))
  }, [])

  const handleAnswer2Change = useCallback((value) => {
    setQuizData((prev) => ({ ...prev, answer2: value }))
  }, [])

  const handleAnswer3Change = useCallback((value) => {
    setQuizData((prev) => ({ ...prev, answer3: value }))
  }, [])

  const handleAnswer4Change = useCallback((value) => {
    setQuizData((prev) => ({ ...prev, answer4: value }))
  }, [])

  const handleAnswer5Change = useCallback((value) => {
    setQuizData((prev) => ({ ...prev, answer5: value }))
  }, [])

  const handleAnswerImageChange = useCallback((file) => {
    setQuizData((prev) => ({ ...prev, answerImageFile: file }))
  }, [])

  const handleAnswerImageFilesChange = useCallback((files) => {
    setQuizData((prev) => ({
      ...prev,
      answerImageFiles: files,
      answerImageFile: files[0] || null,
    }))
  }, [])

  const handleExplanationChange = useCallback((value) => {
    setQuizData((prev) => {
      const result = { ...prev, explanation: value }
      return result
    })
  }, [])

  const handleCheckedStatesChange = useCallback((states) => {
    setQuizData((prev) => ({
      ...prev,
      checkedStates: states,
      isCorrect1: states[0] || false,
      isCorrect2: states[1] || false,
      isCorrect3: states[2] || false,
      isCorrect4: states[3] || false,
      isCorrect5: states[4] || false,
    }))
  }, [])

  const handleExplanationImageChange = useCallback((file) => {
    setQuizData((prev) => ({ ...prev, explanationImageFile: file }))
  }, [])

  const uploadImage = async (file) => {
    const formData = new FormData()
    formData.append('image', file)
    const res = await fetch(`${baseUrl}/img/upload`, {
      method: 'POST',
      body: formData,
    })
    const data = await res.json()
    if (!res.ok || !data.imageUrl) throw new Error('Failed to upload image')
    return data.imageUrl
  }

  const handleSaveQuiz = useCallback(
    async (answerType) => {
      try {
        let finalMeta = { ...chapterMeta }
        if (
          !chapterMeta.depth2 ||
          !chapterMeta.depth3 ||
          !chapterMeta.depth4 ||
          !chapterMeta.depth5
        ) {
          finalMeta = {
            depth2:
              curriculum.mainChapter
                .toString()
                .replace(/[^0-9]/g, '')
                .slice(-1) || '0',
            depth3:
              curriculum.subChapter
                .toString()
                .replace(/[^0-9]/g, '')
                .slice(-1) || '0',
            depth4:
              curriculum.unit
                .toString()
                .replace(/[^0-9]/g, '')
                .slice(-1) || '0',
            depth5:
              curriculum.detailUnit
                .toString()
                .replace(/[^0-9]/g, '')
                .slice(-1) || '0',
            no: curriculum.count || '1',
          }
        }

        const uploadedQuestionImageUrl = quizData.questionImageFile
          ? await uploadImage(quizData.questionImageFile)
          : ''

        const uploadedSubQuestionImageUrl = quizData.subQuestionImageFile
          ? await uploadImage(quizData.subQuestionImageFile)
          : ''

        const uploadedQuestionImageUrl1 = quizData.questionImageFile1
          ? await uploadImage(quizData.questionImageFile1)
          : ''

        const uploadedQuestionImageUrl2 = quizData.questionImageFile2
          ? await uploadImage(quizData.questionImageFile2)
          : ''

        const uploadedExplanationImageUrl = quizData.explanationImageFile
          ? await uploadImage(quizData.explanationImageFile)
          : ''

        const uploadedAnswerImageUrls = []
        for (const file of quizData.answerImageFiles) {
          if (file) {
            const url = await uploadImage(file)
            uploadedAnswerImageUrls.push(url)
          } else {
            uploadedAnswerImageUrls.push('')
          }
        }

        const requestData = {
          LEVEL: curriculum.level.replace('level ', ''),
          MAIN: finalMeta.depth2,
          SUB: finalMeta.depth3,
          UNIT: finalMeta.depth4,
          DETAIL_UNIT: finalMeta.depth5,
          NO: parseInt(curriculum.count) || 1,
          TITLE: quizData.question,
          IMG_URL: uploadedQuestionImageUrl,
          IMG_URL_SUB: uploadedSubQuestionImageUrl,
          IMG_URL2: uploadedQuestionImageUrl2,
          ANSWER1:
            answerType === 'image'
              ? uploadedAnswerImageUrls[0] || ''
              : quizData.answer1,
          ANSWER2:
            answerType === 'image'
              ? uploadedAnswerImageUrls[1] || ''
              : quizData.answer2,
          ANSWER3:
            answerType === 'image'
              ? uploadedAnswerImageUrls[2] || ''
              : quizData.answer3,
          ANSWER4:
            answerType === 'image'
              ? uploadedAnswerImageUrls[3] || ''
              : quizData.answer4,
          ANSWER5:
            answerType === 'image'
              ? uploadedAnswerImageUrls[4] || ''
              : quizData.answer5,
          IS_CORRECT_1: quizData.isCorrect1,
          IS_CORRECT_2: quizData.isCorrect2,
          IS_CORRECT_3: quizData.isCorrect3,
          IS_CORRECT_4: quizData.isCorrect4,
          IS_CORRECT_5: quizData.isCorrect5,
          DESC: quizData.explanation,
          DESC_URL: uploadedExplanationImageUrl,
          TYPE: curriculum.selectedType,
          TYPE2: quizData.type2,
          ANSWER_IMG_URLS: uploadedAnswerImageUrls,
        }

        const result = await insertQuestion(requestData)
        return result
      } catch (error) {
        throw error
      }
    },
    [
      curriculum.level,
      curriculum.mainChapter,
      curriculum.subChapter,
      curriculum.unit,
      curriculum.detailUnit,
      curriculum.count,
      curriculum.selectedType,
      curriculum.questionLabel,
      quizData.question,
      quizData.questionImageFile,
      quizData.questionImageFile1,
      quizData.questionImageFile2,
      quizData.explanationImageFile,
      quizData.answerImageFiles,
      quizData.answer1,
      quizData.answer2,
      quizData.answer3,
      quizData.answer4,
      quizData.answer5,
      quizData.isCorrect1,
      quizData.isCorrect2,
      quizData.isCorrect3,
      quizData.isCorrect4,
      quizData.isCorrect5,
      quizData.explanation,
      chapterMeta.depth2,
      chapterMeta.depth3,
      chapterMeta.depth4,
      chapterMeta.depth5,
      insertQuestion,
    ]
  )

  return {
    curriculum,
    quizData,
    isLoading: isPending,
    error,
    isSuccess,

    handleLevelChange,
    handleMainChapterChange,
    handleSubChapterChange,
    handleUnitChange,
    handleDetailUnitChange,
    handleCountChange,
    handleTypeChange,

    handleQuestionChange,
    handleQuestionImageChange,
    handleSubQuestionImageChange,
    handleQuestionImage1Change,
    handleQuestionImage2Change,
    handleCorrectAnswerChange,
    handleCorrectAnswersChange,
    handleCheckedStatesChange,
    handleAnswer1Change,
    handleAnswer2Change,
    handleAnswer3Change,
    handleAnswer4Change,
    handleAnswer5Change,
    handleAnswerImageChange,
    handleAnswerImageFilesChange,
    handleExplanationChange,
    handleExplanationImageChange,

    handleSaveQuiz,
    levelOptions,
    mainOptions,
    subOptions,
    unitOptions,
    detailUnitOptions,
    handleQuestionLabelChange,
    updateQuestionLabel,
  }
}
